import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["active", "appearance", "children", "disabled", "icon", "meta", "label", "onClick", "onMouseEnter", "onOptionClick", "onOptionHover", "selected", "value"];
import PropTypes from 'prop-types';
import React, { forwardRef, useCallback } from 'react';
import { ListboxOptionLabel } from '../ListboxOptionLabel/ListboxOptionLabel';
import { ListboxOptionMedia } from '../ListboxOptionMedia/ListboxOptionMedia';
import { ListboxOptionMeta } from '../ListboxOptionMeta/ListboxOptionMeta';
import { StyledListboxOption } from './ListboxOption.styles';

/**
 * The default option component for a `Listbox`.
 *
 * **Note:** This component is not meant to be used outside the context of a
 * `Listbox` `renderOption` callback.
 *
 * @since 8.28.0
 */
export var ListboxOption = /*#__PURE__*/forwardRef(function (props, forwardedRef) {
  var active = props.active,
    appearance = props.appearance,
    children = props.children,
    disabled = props.disabled,
    icon = props.icon,
    meta = props.meta,
    label = props.label,
    onClick = props.onClick,
    onMouseEnter = props.onMouseEnter,
    onOptionClick = props.onOptionClick,
    onOptionHover = props.onOptionHover,
    selected = props.selected,
    value = props.value,
    rest = _objectWithoutProperties(props, _excluded);
  var clickCallback = useCallback(function (e) {
    if (onOptionClick && !disabled) {
      onOptionClick(value);
    }
    if (onClick) {
      onClick(e);
    }
  }, [value, onOptionClick, onClick, disabled]);
  var mouseEnterCallback = useCallback(function (e) {
    if (onOptionHover && !disabled) {
      onOptionHover(value);
    }
    if (onMouseEnter) {
      onMouseEnter(e);
    }
  }, [value, onOptionHover, onMouseEnter, disabled]);
  var hasMedia = icon || typeof appearance === 'string';
  var labelNode;
  var mediaNode;
  var metaNode;
  if (typeof children === 'undefined') {
    labelNode = /*#__PURE__*/React.createElement(ListboxOptionLabel, null, label);
    mediaNode = hasMedia ? /*#__PURE__*/React.createElement(ListboxOptionMedia, null, icon) : null;
    metaNode = meta ? /*#__PURE__*/React.createElement(ListboxOptionMeta, null, meta) : null;
  }
  return /*#__PURE__*/React.createElement(StyledListboxOption, _extends({
    appearance: typeof appearance === 'string' ? appearance : undefined,
    "aria-disabled": disabled ? true : undefined,
    "aria-selected": selected ? true : undefined,
    "data-active": active,
    icon: icon,
    onClick: clickCallback,
    onMouseEnter: mouseEnterCallback,
    ref: forwardedRef
  }, rest), mediaNode, labelNode, metaNode, children);
});
ListboxOption.displayName = 'ListboxOption';
ListboxOption.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Marks this as the active option (the option that has focused).
   *
   * This is generally passed down from `Listbox`.
   */
  active: PropTypes.bool,
  /**
   * An optional appearance. If `icon` is also defined, `icon` will take precedence.
   */
  appearance: PropTypes.oneOf(['checkbox', 'radio']),
  /**
   * Set the option as disabled with the
   * [`aria-disabled`](https://www.w3.org/TR/wai-aria-1.2/#aria-disabled) attribute.
   */
  disabled: PropTypes.bool,
  /**
   * Set the option as selected with the
   * [`aria-selected`](https://www.w3.org/TR/wai-aria-1.2/#aria-selected) attribute.
   *
   * This will automatically be set when nested within
   * `Listbox`.
   */
  selected: PropTypes.bool,
  /**
   * In general you will want to use the `label` and `meta` props, but if you need full
   * customization of option content, you can provide your own node with `children`.
   */
  children: PropTypes.node,
  /**
   * An optional `Icon` node. This will take precedence
   * over the `appearance` prop.
   */
  icon: PropTypes.node,
  /**
   * Additional contextual information to display alongside the `label`.
   *
   * Like `label`, it is best to avoid long `meta` values to facilitate understandability for
   * screen reader users.
   */
  meta: PropTypes.string,
  /**
   * The label to display for this option.
   *
   * Avoiding very long option labels facilitates understandability and perceivability for screen
   * reader users. The entire label of an option is spoken as a single unit of speech when the
   * option is read.
   */
  label: PropTypes.node.isRequired,
  /**
   * @ignore
   */
  onClick: PropTypes.func,
  /**
   * Called when an option is clicked with the option value.
   */
  onOptionClick: PropTypes.func,
  /**
   * Called when an option is hovered with the option value.
   */
  onOptionHover: PropTypes.func,
  /**
   * @ignore
   */
  onMouseEnter: PropTypes.func,
  /**
   * https://www.w3.org/TR/wai-aria-1.2/#option
   */
  role: PropTypes.string,
  /**
   * The value of the option.
   */
  value: PropTypes.string.isRequired
} : {};
ListboxOption.defaultProps = {
  role: 'option'
};